import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";

import { Burger } from "../../../assets/icons/Burger";
import { Close } from "../../../assets/icons/Close";
import { TopPanelMenuComponent } from "../../../components/TopPanelMenu";
import { UserSettingsDesktop } from "../../../components/UserSettings/UserSettings";
import {
  ADMIN_SIDEBAR_COLLAPSED,
  AUTH_STORED_ROUTE,
} from "../../../constants/localStorageKeys";
import { useAuth } from "../../../hooks/useAuth";
import { getVisibleAdminMenuGroups } from "../../../routes/admin.routes";
import { getEnvironment } from "../../../utils/environment";
import { isAdminUser } from "../../../utils/isAdminUser";
import { isSuperAdminUser } from "../../../utils/isSuperAdminUser";
import { getCurrentRegion } from "../../../utils/region";
import { safeStorage } from "../../../utils/storage";
import { AdminSidebar } from "../AdminSidebar";
import {
  ContentContainer,
  EnvironmentBanner,
  LayoutShell,
  EnvironmentBannerTitle,
  EnvironmentBannerText,
  MobileMenuToggle,
  NavContentContainer,
  UnauthorizedContainer,
  UnauthorizedTitle,
  UnauthorizedText,
} from "./AdminLayout.styles";

type BannerSeverity = "production" | "uat" | "default";

/**
 * Fixed bar at the bottom of every /admin page telling the SuperAdmin which
 * environment and region this session operates on (EN-5129) — SuperAdmins
 * hold separate credentials per region, so everything done here lands on
 * the shown region's database. Solid attention colors: red on production,
 * amber on UAT, info blue elsewhere.
 */
function AdminEnvironmentBanner() {
  const environment = getEnvironment();
  const region = getCurrentRegion();

  let severity: BannerSeverity = "default";
  if (environment === "production") severity = "production";
  else if (environment === "uat") severity = "uat";

  return (
    <EnvironmentBanner severity={severity}>
      <EnvironmentBannerTitle>
        {environment.toUpperCase()} · {region}
      </EnvironmentBannerTitle>
      <EnvironmentBannerText>
        You are managing {environment} data in the {region} region.
      </EnvironmentBannerText>
    </EnvironmentBanner>
  );
}

function MobileMenuButton({
  isOpen,
  onClick,
}: {
  isOpen: boolean;
  onClick: () => void;
}) {
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 1100);

  useEffect(() => {
    const onResize = () => {
      setIsSmallScreen(window.innerWidth <= 1100);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  if (!isSmallScreen) {
    return null;
  }

  return (
    <MobileMenuToggle type="button" onClick={onClick}>
      {isOpen ? <Close /> : <Burger />}
    </MobileMenuToggle>
  );
}

/**
 * Layout for the /admin section (EN-5129) — the in-portal replacement for
 * the Retool internal tools. Access is restricted to SuperAdmin users with
 * a @patientmpower.com email (see isSuperAdminUser). The reports dashboard
 * that previously lived here moved to /reports (see ReportsLayout).
 */
export function AdminLayout() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    return safeStorage.getItem(ADMIN_SIDEBAR_COLLAPSED) === "true";
  });

  if (!isAuthenticated) {
    safeStorage.setItem(
      AUTH_STORED_ROUTE,
      `${location.pathname}${location.search}`
    );
    return <Navigate to="/login" />;
  }

  if (!isSuperAdminUser()) {
    // Pre-EN-5129 the reports dashboard lived at /admin — send users who can
    // read it (SystemController / LegacyAdmin bookmarks) to its new home
    // instead of a dead end.
    if (isAdminUser()) {
      return <Navigate to="/reports" replace />;
    }

    return (
      <UnauthorizedContainer>
        <UnauthorizedTitle>Access Denied</UnauthorizedTitle>
        <UnauthorizedText>
          You do not have permission to access the admin area. Please contact
          your administrator if you believe this is an error.
        </UnauthorizedText>
      </UnauthorizedContainer>
    );
  }

  const handleLogoClick = () => {
    navigate("/admin");
  };

  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleToggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      safeStorage.setItem(ADMIN_SIDEBAR_COLLAPSED, String(next));
      return next;
    });
  };

  return (
    <>
      <TopPanelMenuComponent onLogoClick={handleLogoClick}>
        <NavContentContainer>
          <MobileMenuButton
            isOpen={isMobileMenuOpen}
            onClick={handleMobileMenuToggle}
          />
        </NavContentContainer>

        <UserSettingsDesktop />
      </TopPanelMenuComponent>

      <AdminSidebar
        items={getVisibleAdminMenuGroups()}
        mobileMenuOpen={isMobileMenuOpen}
        collapsed={isSidebarCollapsed}
        onToggleCollapse={handleToggleSidebar}
      />

      <LayoutShell>
        <ContentContainer
          sidebarState={isSidebarCollapsed ? "collapsed" : "expanded"}
        >
          <Outlet />
        </ContentContainer>

        <AdminEnvironmentBanner />
      </LayoutShell>
    </>
  );
}
